import React, { ChangeEvent, Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { Box, Collapse, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Tooltip } from '@mui/material'
import InfoIcon from "@mui/icons-material/Info";
import { KnowledgeBase } from '../../../Shared-ui/Types/KnowledgeBase';
import { NotSelected } from '../../../Constant/Setting';
import { useDispatch } from 'react-redux';
import { StartLoading, StopLoading } from '../../../Shared-ui/Store/LoadingReducer/LoaderActions';
import BaseInstance from '../../../Services/AxiosInstance/BaseInstance';
import { ShowError } from '../../../Shared-ui/Store/MessageReducer/MsgActions';
import { CHUNK_STRATEGIES, DB_TYPES, EMBEDDINGS } from '../../../Services/Endpoint';
import { EmbeddingModels } from '../../../Shared-ui/Types/common';

interface MyComponentProps {
  showAdvancedOptions: boolean
  knowledgeBase: KnowledgeBase
  setKnowledgeBase: Dispatch<SetStateAction<KnowledgeBase>>
  knowledgeBaseErr: any
}

const AdvancedOptions: FC<MyComponentProps> = (props) => {
  const dispatch = useDispatch()
  // const vectorDB = [{ type: "qdrant", qdrant: { collection_name: "my_collection", url: "http://localhost:6333" } }]
  const [embedder, setEmbedder] = useState<{ type: string; model: string; }[]>([])
  const [chunking, setChunking] = useState<{ name: string; type: string; }[]>([])
  const [vectorDB, setVectorDB] = useState<{ type: string; qdrant: { collection_name: string, url: string } }[]>([])
  // const [vectorDB, setVectorDB] = useState<{ name: string; type: string; }[]>([])


  useEffect(() => {
    getEmbedder()
    getChuncking()
    getDbTypes()
  }, [])


  useEffect(() => {
    if (props.knowledgeBase.type === "website") {
      props.setKnowledgeBase(preVal => ({
        ...preVal,
        config: {
          ...preVal.config,
          chunking: { type: "recursive" },
        }
      }))
    }
  }, [props.knowledgeBase.type])

  function getEmbeddingOptions(embeddingData: Record<string, string[]>): { type: string; model: string }[] {
    const options: { type: string; model: string }[] = [];

    for (const provider in embeddingData) {
      if (Object.prototype.hasOwnProperty.call(embeddingData, provider)) {
        const embeddings = embeddingData[provider];
        for (const embedding of embeddings) {
          options.push({
            type: `${provider}`,
            model: `${embedding}`
          });
        }
      }
    }
    return options;
  }


  function formatChunkingOptions(types: string[]): { name: string; type: string }[] {
    return types.map((type) => ({
      type,
      name: type
        .split("_")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    }));
  }

  function formatDbTypes(dbTypes: string[]): { type: string; qdrant: { collection_name: string, url: string } }[] {
    return dbTypes.map((db) => ({
      type: db,
      qdrant: { collection_name: "my_collection", url: "http://localhost:6333" }
    }));
  }


  const getEmbedder = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(EMBEDDINGS).then((res) => res?.data);
      const embedderArr = getEmbeddingOptions(data)
      setEmbedder(embedderArr)
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText));
    } finally {
      dispatch(StopLoading());
    }
  }


  const getChuncking = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(CHUNK_STRATEGIES).then((res) => res?.data);
      const chunks = formatChunkingOptions(data)
      setChunking(chunks)
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText));
    } finally {
      dispatch(StopLoading());
    }
  }

  const getDbTypes = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(DB_TYPES).then((res) => res?.data);
      const dbtypes = formatDbTypes(data)
      setVectorDB(dbtypes)
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText));
    } finally {
      dispatch(StopLoading());
    }
  }


  const handleChangeConfig = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    const { name, value } = event.target
    if (name == "embedder" || name == "chunking" || name == "vector_db") {
      props.setKnowledgeBase(preVal => ({ ...preVal, config: { ...preVal.config, [name]: JSON.parse(value) } }))
    } else {
      props.setKnowledgeBase(preVal => ({ ...preVal, config: { ...preVal.config, [name]: value } }))
    }
    console.log(props.knowledgeBase)
  };

  return (
    <Collapse in={props.showAdvancedOptions} sx={{ mt: 2, }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      <FormControl fullWidth sx={{ mb: .5, flex: "1 1 48%" }}>
        <InputLabel shrink htmlFor="chunkSize" sx={{ background: "#fff", px: 0.5 }}>
          Chunk Size
        </InputLabel>
        <TextField
          required
          id="chunk_size"
          name="chunk_size"
          type='number'
          value={props.knowledgeBase.config.chunk_size}
          onChange={handleChangeConfig}
          inputProps={{ maxLength: 8 }}
          placeholder="Enter Chunck Size"
          variant="outlined" />
      </FormControl>
      <FormControl fullWidth sx={{ mb: .5 , flex: "1 1 48%"}}>
        <InputLabel shrink htmlFor="chunkOverlap" sx={{ background: "#fff", px: 0.5 }}>
          Chunk Overlap
        </InputLabel>
        <TextField
          required
          id="chunk_overlap"
          name="chunk_overlap"
          type='number'
          value={props.knowledgeBase.config.chunk_overlap}
          onChange={handleChangeConfig}
          inputProps={{ maxLength: 8 }}
          placeholder="Enter Chunck overlap"
          variant="outlined" />
      </FormControl>
      <FormControl fullWidth sx={{ mb: .5, flex: "1 1 48%" }} error={props.knowledgeBaseErr.chunking.value}>
        <InputLabel shrink htmlFor="chunkingStrategy" sx={{ background: "#fff", px: 0.5 }}>
          Chunking Strategy
        </InputLabel>

        <Select id="chunkingStrategy" name="chunking"
          value={JSON.stringify(props.knowledgeBase.config.chunking)} onChange={handleChangeConfig}>
          {chunking.map((items, index) => (
            <MenuItem value={JSON.stringify({ type: items.type })} key={index}>{items.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: .5, flex: "1 1 48%" }} error={props.knowledgeBaseErr.vector_db.value}>
        <InputLabel shrink htmlFor="vectorDatabase" sx={{ background: "#fff", px: 0.5 }}>
          Vector Database
        </InputLabel>
        <Select id="vector_db" name="vector_db"
          value={JSON.stringify(props.knowledgeBase.config.vector_db)} onChange={handleChangeConfig}>
          {vectorDB.map((items, index) => (
            <MenuItem value={JSON.stringify(items)} key={index}>{String(items.type).toUpperCase()}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: .5 }} error={props.knowledgeBaseErr.embedder.value}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
          <InputLabel shrink htmlFor="embeddingModel" sx={{ background: "#fff", px: 0.5 }}>
            Embeddings Provider & Model
            <Tooltip title="Embedding models convert text into vector representations for semantic search.">
              <InfoIcon sx={{ fontSize: 18, cursor: "pointer", color: "#78909C" }} />
            </Tooltip>
          </InputLabel>

        </Box>
        <Select id="chunkingStrategy" name="embedder"
          value={JSON.stringify(props.knowledgeBase.config.embedder)} onChange={handleChangeConfig}>
          {embedder.map((items, index) => (
            <MenuItem value={JSON.stringify(items)} key={index}>{items.model}</MenuItem>
          ))}
        </Select>
      </FormControl>
      </Box>
    </Collapse>
  )
}

export default AdvancedOptions